import { cookieJar } from './cookieJar';

export const accountHomeBaseUrl = import.meta.env.VITE_ACCOUNT_HOME_BASE_URL;
export const cnufeBaseUrl = import.meta.env.VITE_CNUFE_HOME;
export const blocBaseUrl = import.meta.env.VITE_BLOC_HOME_URL;
export const cnuappApiBaseUrl = import.meta.env.VITE_CNUAPP_API_BASE_URL;
export const orchestratorBaseUrl = import.meta.env.VITE_ORCHESTRATOR_BASE_URL;
export const loginServerBaseUrl = import.meta.env.VITE_LOGIN_SERVER_BASE_URL;

type QueryParamsT = Record<string, string>;

// Orchestrator flowIds are UUIDs. Callers that put a customer-suppliable flowId (e.g. `?flowId=`
// on the URL) into a page should check this before stashing/using it — see RootLayout. This is
// belt-and-braces alongside encodeURIComponent below, not a substitute for it: an unencoded
// flowId containing `/` or `?` can redirect the request (with the customer's own bearer token)
// to an arbitrary orchestrator path, regardless of what shape a caller expected it to have.
const FLOW_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isValidFlowId = (flowId: string): boolean => FLOW_ID_PATTERN.test(flowId);

const urlWithQueryParams = (url: string, queryParams?: QueryParamsT) => {
  if (!queryParams) {
    return url;
  }

  const params = new URLSearchParams(queryParams);

  return `${url}?${params.toString()}`;
};

export function cnuappApiEndpoint(path: `/${string}`, queryParams?: QueryParamsT): string {
  return urlWithQueryParams(`${cnuappApiBaseUrl}${path}`, queryParams);
}

export function orchestratorEndpoint(path: `/${string}`, queryParams?: QueryParamsT): string {
  return urlWithQueryParams(`${orchestratorBaseUrl}${path}`, queryParams);
}

export const apiEndpoints = {
  accountSummary: (accountId: string) => cnuappApiEndpoint(`/v4/secure/accounts/${accountId}`),
  activeLoanSummary: (accountId: string) => cnuappApiEndpoint(`/v4/secure/accounts/${accountId}/loans`, { active: 'true' }),
  blocAccountSummary: (accountId: string) => `${blocBaseUrl}/api/v1/accounts/${accountId}/summary`,
  blocActiveLoanSummary: (accountId: string, loanNumber: string) => (
    `${blocBaseUrl}/api/v1/accounts/${accountId}/loans/${loanNumber}/summary`
  ),
  // Placeholder endpoint. The real contract route is not wired up yet; this
  // returns a fake path so callers/mocks can resolve.
  contract: () => '/mock/contract',
  currentStep: (flowId: string) => (
    orchestratorEndpoint(`/orchestrator/${encodeURIComponent(flowId)}/current_step`)
  ),
  displayLoanOffer: (flowId: string) => (
    orchestratorEndpoint(`/loan_application/${encodeURIComponent(flowId)}/display_loan_offer`)
  ),
  messages: (accountId: string, queryParams?: QueryParamsT) => (
    cnuappApiEndpoint(`/v4/secure/accounts/${accountId}/messages`, queryParams)
  ),
  submitContract: (flowId: string) => (
    orchestratorEndpoint(`/loan_application/${encodeURIComponent(flowId)}/contract`)
  ),
  token: `${loginServerBaseUrl}/token`,
};

export const uiRoutes = {
  aboutUs: `${cnufeBaseUrl}/about-us`,
  accountActivity: `${cnufeBaseUrl}/details/activity`,
  accountHistory: `${cnufeBaseUrl}/details/history`,
  accountHome: `${cnufeBaseUrl}/account_home`,
  blocAccountSummary: `${blocBaseUrl}/my_account/summary`,
  blocBankInfo: `${blocBaseUrl}/my_info/bank`,
  blocCashAdvance: (loanNumber: string) => `${blocBaseUrl}/loans/${loanNumber}/draw`,
  blocEmploymentInfo: `${blocBaseUrl}/my_info/employment`,
  blocFeeSaverStatus: (loanNumber: string) => `${blocBaseUrl}/loans/${loanNumber}/fee_saver_status`,
  blocLoanDetails: `${blocBaseUrl}/my_account/loan_details`, // used in minimum payment estimator
  blocLogOut: `${blocBaseUrl}/logout`,
  blocMakePayment: `${blocBaseUrl}/my_account/make_a_payment`,
  blocPersonalInfo: `${blocBaseUrl}/my_info/personal`,
  blocStatements: (loanNumber: string) => `${blocBaseUrl}/loans/${loanNumber}/statements`,
  blog: `${cnufeBaseUrl}/blog`,
  californiaPrivacyPolicy: `${cnufeBaseUrl}/california-privacy-policy`,
  cashAdvance: `${cnufeBaseUrl}/cash-advance`,
  changePaymentDate: `${cnufeBaseUrl}/due_date_adjustment/show`,
  contact: `${cnufeBaseUrl}/profile/contact`,
  contactUs: `${cnufeBaseUrl}/contact-us.html`,
  contractSubmitted: `${cnufeBaseUrl}/contracts/submitted`,
  documents: `${cnufeBaseUrl}/profile/documents`,
  employment: `${cnufeBaseUrl}/profile/employment`,
  faq: `${cnufeBaseUrl}/faq`,
  fastCash: `${cnufeBaseUrl}/quick-loans`,
  installmentLoans: `${cnufeBaseUrl}/installment-loans`,
  legacyContract: (loanNumber: string) => `${blocBaseUrl}/loans/${loanNumber}/contract`,
  lineOfCredit: `${cnufeBaseUrl}/personal-line-of-credit`,
  // Where web-acquisitions sends the customer when a mid-flow cnuapp call comes back
  // unauthenticated (session expired) and they need to sign back in.
  loginInactivity: `${cnufeBaseUrl}/login?error=inactivity`,
  logOut: `${cnufeBaseUrl}/logout`,
  makePayment: `${cnufeBaseUrl}/payments/new`,
  messageDetail: '/message-detail',
  messages: '/messages',
  onlineLoans: `${cnufeBaseUrl}/online-loans`,
  onlineSecurity: `${cnufeBaseUrl}/consumer-notices`,
  optOut: `${cnufeBaseUrl}/opt_out`,
  oregonPrivacyPolicy: '',
  password: `${cnufeBaseUrl}/profile/password`,
  paydayLoanAlternatives: `${cnufeBaseUrl}/payday-loan-alternatives`,
  paymentMethods: `${cnufeBaseUrl}/payments/details`,
  personal: `${cnufeBaseUrl}/profile/personal`,
  privacyPolicy: `${cnufeBaseUrl}/privacy-policy`,
  products: `${cnufeBaseUrl}/products`,
  ratesAndTerms: `${cnufeBaseUrl}/rates-and-terms`,
  serviceOfProcess: `${cnufeBaseUrl}/service-of-process`,
  statements: `${cnufeBaseUrl}/details/statements`,
  termsOfUse: `${cnufeBaseUrl}/terms-of-use`,
};

// Because logins can come from multiple sources, resolve tenant at request time for token refresh calls.
export const getLoginServerTenantID = () => cookieJar.getTenant()?.[0] || import.meta.env.VITE_LOGIN_SERVER_TENANT_ID;
